import {Component, signal} from '@angular/core';
import {Subscription} from 'rxjs';
import {environment} from '../environments/environment';
import {NgSelectConfig} from '@ng-select/ng-select';
import {RouterOutlet} from '@angular/router';
import {LocalStorage} from './storage.class';
import {AuthService} from './services/auth/auth.service';
import {WebsocketService} from './services/websocket/websocket.service';
import {CodaltComponent} from './codalt.component';
import {PlatformService} from './services/platform.service';
import {Checkpoint} from './classes/checkpoint';
import {CheckpointStatusOrbsEnum} from './classes/checkpoint-status-orbs.enum';
import {Platform} from './classes/platform';
import {LeaveService} from './services/leave.service';
import {AuthorisationService} from './services/auth/authorisation.service';
import {SeireStatusOrbs, SidebarComponent} from './shared/ui/sidebar/sidebar.component';
import {CompanyService} from './services/company.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        RouterOutlet,
        SidebarComponent
    ]
})
export class AppComponent extends CodaltComponent {

    environment = environment;
    tokenExpireTimeout = null;
    currentUser = LocalStorage.getUser();
    platforms: Platform[] = [];
    private platformSubscription = new Subscription();
    LeaveService = LeaveService;

    readonly seireStatus = signal<SeireStatusOrbs>({
        production: CheckpointStatusOrbsEnum.inactive,
        health: CheckpointStatusOrbsEnum.inactive,
        check: CheckpointStatusOrbsEnum.inactive,
        acceptance: CheckpointStatusOrbsEnum.inactive,
    });

    public constructor(
        private authService: AuthService,
        private companyService: CompanyService,
        private leaveService: LeaveService,
        private ngSelectConfig: NgSelectConfig,
        private platformService: PlatformService,
        private websocketService: WebsocketService,
    ) {
        super();
        this.ngSelectConfig.notFoundText = 'Geen items gevonden';
        this.ngSelectConfig.loadingText = 'Aan het laden...';
        this.ngSelectConfig.clearAllText = 'Alles wissen';
        this.ngSelectConfig.typeToSearchText = 'Typ om te zoeken';
        this.ngSelectConfig.fixedPlaceholder = true;
    }

    ngOnInit() {
        this.checkAuth();
        if (typeof window !== 'undefined' && window.addEventListener) {
            window.addEventListener('storage', () => {
                this.checkAuth();
            }, false);
            const recordActivity = () => LocalStorage.setLastActivity(new Date().toISOString());
            window.addEventListener('click', recordActivity, {passive: true});
            window.addEventListener('keydown', recordActivity, {passive: true});
        }

        this.subscriptions.add(
            this.authService.loggedIn.subscribe(isLoggedIn => {
                this.currentUser = LocalStorage.getUser();
                if (isLoggedIn) {
                    this.loadPlatforms();
                    this.loadCustomerColors();
                }
            })
        );

        if (!!LocalStorage.getUserToken()) {
            this.loadPlatforms();
            this.loadCustomerColors();
        }

        if (!!LocalStorage.getUserToken()) {
            const currentRoles = JSON.stringify(LocalStorage.getUser()?.roles);
            this.subscriptions.add(
                this.authService.getUserDetails().subscribe(user => {
                    const newRoles = JSON.stringify(user.roles);
                    if (currentRoles !== newRoles) {
                        window.location.reload();
                    }
                })
            );
        }

        if (AuthorisationService.hasFeature(this.Features.LeaveUnapproved)) {
            this.subscriptions.add(
                this.leaveService.getUnapprovedLeaves().subscribe()
            );
        }
    }

    private getLevelStatus(count: number, errors: number, warnings: number): CheckpointStatusOrbsEnum {
        if (!count) {
            return CheckpointStatusOrbsEnum.inactive;
        }
        if (errors !== 0) {
            return CheckpointStatusOrbsEnum.error;
        } else if (warnings !== 0) {
            return CheckpointStatusOrbsEnum.warning;
        }
        return CheckpointStatusOrbsEnum.success;
    }

    private isOutdated(lastInstance: Checkpoint[] | null | undefined): boolean {
        if (!lastInstance?.length) {
            return false;
        }
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);
        return new Date(lastInstance[0].updated_at) < oneDayAgo;
    }

    private loadPlatforms() {
        this.platformSubscription.unsubscribe();
        this.platformSubscription = this.platformService.getPlatformsLive().subscribe({
            next: (platforms) => {
                this.platforms = platforms.map(p => ({...p}));
                this.seireNavbar();
            },
            error: err => console.error(err)
        });
        this.subscriptions.add(this.platformSubscription);
    }

    private loadCustomerColors() {
        if (AuthorisationService.hasFeature(this.Features.CompaniesColors)) {
            this.subscriptions.add(this.companyService.loadColors().subscribe());
        }
    }

    private seireNavbar() {
        type LevelKey = 'production' | 'health' | 'check' | 'acceptance';

        const totals: Record<LevelKey, { count: number; errors: number; warnings: number }> = {
            production: {count: 0, errors: 0, warnings: 0},
            health: {count: 0, errors: 0, warnings: 0},
            check: {count: 0, errors: 0, warnings: 0},
            acceptance: {count: 0, errors: 0, warnings: 0},
        };

        this.platforms.forEach(platform => {
            const levels: { key: LevelKey; count: number; errors: number; warnings: number; lastInstance: Checkpoint[] | null | undefined }[] = [
                {
                    key: 'production',
                    count: +platform.checkpoint_production_count,
                    errors: +platform.checkpoint_production_error_count,
                    warnings: +platform.checkpoint_production_warning_count,
                    lastInstance: platform.checkpoint_production_last_instance
                },
                {
                    key: 'health',
                    count: +platform.checkpoint_health_count,
                    errors: +platform.checkpoint_health_error_count,
                    warnings: +platform.checkpoint_health_warning_count,
                    lastInstance: platform.checkpoint_health_last_instance
                },
                {
                    key: 'check',
                    count: +platform.checkpoint_check_count,
                    errors: +platform.checkpoint_check_error_count,
                    warnings: +platform.checkpoint_check_warning_count,
                    lastInstance: platform.checkpoint_check_last_instance
                },
                {
                    key: 'acceptance',
                    count: +platform.checkpoint_acceptance_count,
                    errors: +platform.checkpoint_acceptance_error_count,
                    warnings: +platform.checkpoint_acceptance_warning_count,
                    lastInstance: platform.checkpoint_acceptance_last_instance
                },
            ];

            levels.forEach(({key, count, errors, warnings, lastInstance}) => {
                if (!count) {
                    return;
                }
                totals[key].count += count;
                totals[key].errors += errors + (this.isOutdated(lastInstance) ? 1 : 0);
                totals[key].warnings += warnings;
            });
        });

        this.seireStatus.update(status => ({
            ...status,
            production: this.getLevelStatus(totals.production.count, totals.production.errors, totals.production.warnings),
            health: this.getLevelStatus(totals.health.count, totals.health.errors, totals.health.warnings),
            check: this.getLevelStatus(totals.check.count, totals.check.errors, totals.check.warnings),
            acceptance: this.getLevelStatus(totals.acceptance.count, totals.acceptance.errors, totals.acceptance.warnings),
        }));
    }

    private checkAuth() {
        if (this.tokenExpireTimeout) {
            clearInterval(this.tokenExpireTimeout);
        }
        if (!LocalStorage.getTokenDate()) {
            return;
        }
        this.tokenExpireTimeout = setInterval(() => {
            const oneWeekMs = 14 * 24 * 60 * 60 * 1000;
            const lastActivity = LocalStorage.getLastActivity();
            if (lastActivity && (new Date().getTime() - new Date(lastActivity).getTime()) > oneWeekMs) {
                console.log('Logging out due to 2 weeks of inactivity');
                this.authService.logout();
                return;
            }

            const tokenValidUntil = new Date(LocalStorage.getTokenDate());
            tokenValidUntil.setSeconds(tokenValidUntil.getSeconds() + (+LocalStorage.getExpireTimeToken()));
            const oneHourMs = 60 * 60 * 1000;
            if (tokenValidUntil.getTime() - new Date().getTime() < oneHourMs) {
                console.log('Refreshing token because it times out', tokenValidUntil);
                this.subscriptions.add(
                    this.authService.refresh().subscribe({
                        next: () => this.websocketService.disconnect(),
                        error: () => console.warn('Token refresh failed, session will expire when the token does')
                    })
                );
            }
        }, 60 * 60 * 1000); // check every hour
    }
}
